import type { Construct } from "@apf/site-publication";
import type { AccentureEdition } from "@apf/site-publication";
import { ProofBadge } from "../components/Shell.tsx";
import type { EditionId } from "../edition.ts";

export function ConstructPage({
  construct,
  edition,
  accentureAdaptations,
}: {
  construct: Construct;
  edition: EditionId;
  accentureAdaptations?: AccentureEdition["constructAdaptations"];
}) {
  const adaptation = accentureAdaptations?.find((a) => a.coreRef === construct.id);

  return (
    <div>
      {/* Header */}
      <div className="wrap" style={{ paddingTop: 56, paddingBottom: 32 }}>
        <span className="eyebrow">{construct.functionLabel}</span>
        <h1 style={{ fontSize: "clamp(44px, 5vw, 72px)" }}>{construct.name}</h1>
        <p style={{ fontSize: 18, color: "var(--muted)", maxWidth: 640 }}>{construct.summary}</p>
        {adaptation && (
          <div style={{
            marginTop: 28,
            padding: "16px 20px",
            background: "var(--brandwash)",
            borderLeft: "3px solid var(--brand)",
            fontSize: 14,
            color: "var(--muted)",
          }}>
            <strong>Accenture Edition:</strong> {adaptation.methodNote}
          </div>
        )}
      </div>

      {/* Modules */}
      <section className="section" aria-labelledby={`${construct.id}-modules-heading`}>
        <div className="wrap">
          <h2 id={`${construct.id}-modules-heading`}>Business modules</h2>
          <div style={{ marginTop: 24 }}>
            {construct.modules.map((mod) => (
              <div
                key={mod.id}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 16,
                  padding: "18px 0",
                  borderTop: "1px solid var(--line)",
                }}
              >
                <div>
                  <p style={{ margin: 0, fontWeight: 500 }}>{mod.name}</p>
                  {edition === "accenture" && adaptation && (
                    <span className="small">
                      {adaptation.methodNote ? "Accenture method" : "Core module"}
                    </span>
                  )}
                </div>
                <span className={`status ${mod.standing}`}>{
                  mod.standing === "reference" ? "Demonstrable"
                  : mod.standing === "defined" ? "Defined"
                  : "Roadmap"
                }</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Proof */}
      <section className="section" aria-labelledby={`${construct.id}-proof-heading`}>
        <div className="wrap">
          <h2 id={`${construct.id}-proof-heading`}>Standing</h2>
          <ProofBadge proofClass={construct.proofClass} />
          <p style={{ marginTop: 16, color: "var(--muted)", maxWidth: 640 }}>{construct.outcome}</p>
        </div>
      </section>

      {/* Changes */}
      {construct.changes.length > 0 && (
        <section className="section" aria-labelledby={`${construct.id}-changes-heading`} style={{ borderBottom: "none" }}>
          <div className="wrap">
            <h2 id={`${construct.id}-changes-heading`}>What changes</h2>
            <div className="before-after">
              {construct.changes.map((ch) => (
                <div key={ch.id}>
                  <h3 style={{ fontSize: 18, marginBottom: 12 }}>{ch.title}</h3>
                  <div className="before-after-pair">
                    <div style={{ padding: 24, background: "var(--surface)" }}>
                      <span className="eyebrow" style={{ color: "var(--muted)" }}>Before</span>
                      <p style={{ fontSize: 14, color: "var(--muted)", margin: 0 }}>{ch.before}</p>
                    </div>
                    <div style={{ padding: 24, background: "var(--brandwash)" }}>
                      <span className="eyebrow">After</span>
                      <p style={{ fontSize: 14, margin: 0 }}>{ch.after}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
